import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import axios from 'axios';
import gsap from 'gsap';
import TextScramble from '../components/TextScramble';
import ScrollReveal from '../components/ScrollReveal';
import ProductCard from '../components/ProductCard';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Marquee() {
  const items = ['Nettoyant', 'S\u00e9rum', 'Cr\u00e8me', 'SPF', 'Gommage', 'Masque', 'Collag\u00e8ne', 'Huile', 'Tonique'];
  return (
    <div className="overflow-hidden py-6 border-y border-black/5">
      <div className="marquee-track">
        {[...items, ...items, ...items, ...items].map((item, i) => (
          <span key={i} className="text-7xl sm:text-8xl lg:text-9xl font-serif font-light text-black/[0.04] whitespace-nowrap mx-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [bestSellers, setBestSellers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const { products: recentlyViewed } = useRecentlyViewed();
  const heroRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroImgRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/products?best_seller=true`).then(res => setBestSellers(res.data)).catch(console.error);
    axios.get(`${API}/products`).then(res => setAllProducts(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power4.out' }});
    if (heroTextRef.current) {
      tl.fromTo(heroTextRef.current.children, { opacity: 0, y: 80 }, { opacity: 1, y: 0, duration: 1.2, stagger: 0.15 }, 0.3);
    }
    if (heroImgRef.current) {
      tl.fromTo(heroImgRef.current, { clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0% 0 0 0)', duration: 1.4 }, 0.2);
    }
  }, []);

  return (
    <div data-testid="home-page">
      {/* ═══ HERO ═══ */}
      <section ref={heroRef} data-testid="hero-section" className="min-h-screen relative overflow-hidden bg-[#F5F0E8]">
        {/* Decorative vertical lines */}
        <div className="hero-line" style={{ left: '25%' }} />
        <div className="hero-line" style={{ left: '50%' }} />
        <div className="hero-line" style={{ left: '75%' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-start">
            {/* Left: Text */}
            <div ref={heroTextRef} className="pt-4 lg:pt-16 lg:pr-16">
              <div>
                <span className="inline-block text-[10px] uppercase tracking-[0.5em] font-bold text-black/30 border border-black/10 px-4 py-2 mb-8">
                  Balmart &mdash; Soins Premium
                </span>
              </div>
              <div>
                <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-serif font-light tracking-[-0.04em] leading-[0.9] text-black" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Sublimez<br/>
                  <span className="italic" style={{ color: '#B80000' }}>Votre</span><br/>
                  Beaut&eacute;
                </h1>
              </div>
              <div>
                <p className="mt-8 text-sm text-black/45 leading-[1.8] max-w-sm">
                  Des formules scientifiques distill&eacute;es &agrave; leur essence. Chaque ingr&eacute;dient m&eacute;rite sa place. Z&eacute;ro compromis, r&eacute;sultats visibles.
                </p>
              </div>
              <div>
                <div className="mt-10 flex flex-wrap items-center gap-4 sm:gap-6">
                  <Link to="/shop" data-testid="shop-collection-btn" className="btn-primary inline-flex items-center gap-3">
                    D&eacute;couvrir
                  </Link>
                  <Link to="/routine" data-testid="home-routine-btn" className="inline-flex items-center text-xs uppercase tracking-[0.2em] font-bold text-black border border-black px-6 py-4 hover:bg-black hover:text-white transition-colors">
                    La Routine
                  </Link>
                </div>
              </div>
              {/* Trust badges strip */}
              <div data-testid="home-trust-badges" className="mt-12 pt-8 border-t border-black/10 grid grid-cols-3 gap-4 max-w-md">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-black/60">Livraison Dakar</p>
                  <p className="text-xs text-black/40 mt-1">24&nbsp;à&nbsp;48h</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-black/60">Paiement</p>
                  <p className="text-xs text-black/40 mt-1">Wave · OM · Free</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-black/60">Formules</p>
                  <p className="text-xs text-black/40 mt-1">100% véganes</p>
                </div>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div ref={heroImgRef} className="relative">
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1764662028080-227b6ccc808f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxibGFjayUyMHdvbWFuJTIwZ2xvd2luZyUyMHNraW4lMjBiZWF1dHklMjBwb3J0cmFpdCUyMHN0dWRpb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Balmart"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge — fixed position inside photo container */}
              <div className="absolute left-4 sm:left-8 bottom-6 sm:bottom-10 bg-[#F5F0E8] shadow-2xl px-5 py-3 z-20">
                <p className="text-3xl font-serif font-light leading-none" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#B80000' }}>98%</p>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/40 mt-1 whitespace-nowrap">Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ MARQUEE ═══ */}
      <Marquee />

      {/* ═══ ABOUT STRIP ═══ */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1753945967432-5a520d796e1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBza2luY2FyZSUyMGNyZWFtJTIwdGV4dHVyZSUyMGNsb3NldXAlMjBtYWNyb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85"
                    alt="Texture"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay text */}
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/60 to-transparent">
                  <p className="text-white/60 text-xs uppercase tracking-[0.3em]">Formule Pure</p>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-6">Notre Philosophie</p>
                <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.1]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  La science au service<br/>de la <span className="italic" style={{ color: '#B80000' }}>beaut&eacute;</span>
                </h2>
                <p className="mt-8 text-sm text-black/45 leading-[1.8] max-w-md">
                  Chaque produit Balmart est le fruit de recherches avanc&eacute;es en dermatologie. Nous croyons qu'une peau saine commence par des ingr&eacute;dients purs, sans compromis.
                </p>
                <div className="mt-10 flex gap-12">
                  <div>
                    <p className="text-4xl font-serif font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>12</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/30 mt-1">Produits</p>
                  </div>
                  <div>
                    <p className="text-4xl font-serif font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>3</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/30 mt-1">Cat&eacute;gories</p>
                  </div>
                  <div>
                    <p className="text-4xl font-serif font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#B80000' }}>100%</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/30 mt-1">Naturel</p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ BEST SELLERS ═══ */}
      <section data-testid="best-sellers-section" className="py-24 sm:py-32 bg-[#EDE8DE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-4">S&eacute;lection</p>
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Meilleures Ventes
                </h2>
              </div>
              <Link to="/shop" data-testid="view-all-link" className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors border-b border-black/20 pb-1 hidden sm:block">
                Voir Tout &rarr;
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {bestSellers.map((product, i) => (
              <ScrollReveal key={product.product_id} delay={i * 0.12}>
                <ProductCard product={product} />
              </ScrollReveal>
            ))}
          </div>

          <div className="mt-12 text-center sm:hidden">
            <Link to="/shop" className="btn-secondary inline-block">Voir Tout</Link>
          </div>
        </div>
      </section>

      {/* ═══ SOIN DE VISAGE ═══ */}
      {allProducts.filter(p => p.product_type === 'Soin de visage').length > 0 && (
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div className="flex items-end justify-between mb-16">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-4">Cat&eacute;gorie</p>
                  <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Soin de <span className="italic" style={{ color: '#B80000' }}>Visage</span>
                  </h2>
                </div>
                <Link to="/shop?type=Soin%20de%20visage" className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors border-b border-black/20 pb-1 hidden sm:block">
                  Voir Tout &rarr;
                </Link>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {allProducts.filter(p => p.product_type === 'Soin de visage').slice(0, 3).map((product, i) => (
                <ScrollReveal key={product.product_id} delay={i * 0.15}>
                  <ProductCard product={product} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ SOIN DE CORPS ═══ */}
      {allProducts.filter(p => p.product_type === 'Soin de corps').length > 0 && (
        <section className="py-24 sm:py-32 bg-[#EDE8DE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div className="flex items-end justify-between mb-16">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-4">Cat&eacute;gorie</p>
                  <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Soin de <span className="italic" style={{ color: '#B80000' }}>Corps</span>
                  </h2>
                </div>
                <Link to="/shop?type=Soin%20de%20corps" className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors border-b border-black/20 pb-1 hidden sm:block">
                  Voir Tout &rarr;
                </Link>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-10">
              {allProducts.filter(p => p.product_type === 'Soin de corps').slice(0, 2).map((product, i) => (
                <ScrollReveal key={product.product_id} delay={i * 0.15}>
                  <ProductCard product={product} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ COMPLÉMENTS ALIMENTAIRES ═══ */}
      {allProducts.filter(p => p.product_type === 'Compl\u00e9ment alimentaire').length > 0 && (
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div className="flex items-end justify-between mb-16">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-4">Cat&eacute;gorie</p>
                  <h2 className="font-serif text-4xl sm:text-5xl tracking-tight font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Compl&eacute;ments <span className="italic" style={{ color: '#B80000' }}>Alimentaires</span>
                  </h2>
                </div>
                <Link to="/shop?type=Compl%C3%A9ment%20alimentaire" className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 hover:text-black transition-colors border-b border-black/20 pb-1 hidden sm:block">
                  Voir Tout &rarr;
                </Link>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {allProducts.filter(p => p.product_type === 'Compl\u00e9ment alimentaire').slice(0, 3).map((product, i) => (
                <ScrollReveal key={product.product_id} delay={i * 0.15}>
                  <ProductCard product={product} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ FULL-WIDTH IMAGE BREAK ═══ */}
      <section className="relative h-[60vh] sm:h-[70vh] overflow-hidden">
        <ScrollReveal className="h-full">
          <img
            src="https://images.unsplash.com/photo-1613730317319-bc2d10ae54ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxibGFjayUyMHdvbWFuJTIwZ2xvd2luZyUyMHNraW4lMjBiZWF1dHklMjBwb3J0cmFpdCUyMHN0dWRpb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85"
            alt="Beauty"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-xs uppercase tracking-[0.5em] font-bold text-white/30 mb-6">La Routine Balmart</p>
              <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl text-white font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Trois &eacute;tapes.<br/><span className="italic" style={{ color: '#B80000' }}>Des r&eacute;sultats.</span>
              </h2>
              <Link to="/routine" className="inline-block mt-10 text-xs uppercase tracking-[0.2em] font-bold text-white/50 hover:text-white transition-colors border-b border-white/20 pb-1">
                D&eacute;couvrir &rarr;
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ 3-STEP PREVIEW ═══ */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/5">
            {[
              { num: '01', title: 'Nettoyer', desc: 'Pr\u00e9parez votre peau en douceur.' },
              { num: '02', title: 'Traiter', desc: 'Actifs concentr\u00e9s en profondeur.' },
              { num: '03', title: 'Hydrater', desc: 'Scellez et prot\u00e9gez.' }
            ].map((step, i) => (
              <ScrollReveal key={step.num} delay={i * 0.2}>
                <div className={`p-10 sm:p-14 text-center ${i < 2 ? 'md:border-r border-b md:border-b-0 border-black/5' : ''}`}>
                  <span className="text-6xl sm:text-7xl font-serif font-light block mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: i === 1 ? '#B80000' : 'rgba(0,0,0,0.06)' }}>
                    {step.num}
                  </span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-normal mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-xs text-black/40 leading-relaxed max-w-[200px] mx-auto">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TEXTURE MOSAIC ═══ */}
      <section className="py-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {[
            'https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBza2luY2FyZSUyMGNyZWFtJTIwdGV4dHVyZSUyMGNsb3NldXAlMjBtYWNyb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85',
            'https://images.unsplash.com/photo-1728994062543-74a1dc2c9392?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBza2luY2FyZSUyMGNyZWFtJTIwdGV4dHVyZSUyMGNsb3NldXAlMjBtYWNyb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85',
            'https://images.unsplash.com/photo-1741017778557-31eaf775ce0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBza2luY2FyZSUyMGNyZWFtJTIwdGV4dHVyZSUyMGNsb3NldXAlMjBtYWNyb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85',
            'https://images.unsplash.com/photo-1753945967432-5a520d796e1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBza2luY2FyZSUyMGNyZWFtJTIwdGV4dHVyZSUyMGNsb3NldXAlMjBtYWNyb3xlbnwwfHx8fDE3NzY0MDM0MjN8MA&ixlib=rb-4.1.0&q=85'
          ].map((src, i) => (
            <ScrollReveal key={i} delay={i * 0.1}>
              <div className="aspect-square overflow-hidden group">
                <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ═══ TESTIMONIALS — Les Balmartistes parlent ═══ */}
      <section data-testid="testimonials-section" className="py-20 sm:py-28 border-t border-black/10" style={{ backgroundColor: '#FFFBF4' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-14">
              <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-black/25 mb-3">Paroles de Dakar</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Les Balmartistes parlent
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                name: 'Aïssatou D.',
                location: 'Plateau, Dakar',
                rating: 5,
                quote: "Je cherchais un sérum qui tienne ses promesses. Après 3 semaines, ma peau est transformée. Livraison le lendemain, emballage digne d'une maison parisienne.",
                product: 'Sérum Anti-Âge Intensif',
              },
              {
                name: 'Mariama S.',
                location: 'Almadies',
                rating: 5,
                quote: "L'eau micellaire est devenue mon indispensable du soir. Douce, efficace, et tellement mieux que ce que je prenais en pharmacie importée.",
                product: 'Eau Micellaire Apaisante',
              },
              {
                name: 'Fatou B.',
                location: 'Mermoz',
                rating: 5,
                quote: "Le paiement Wave marche nickel et je reçois chaque commande en 24h. Balmart, c'est du luxe accessible pour nous les Sénégalaises.",
                product: 'Cliente fidèle',
              },
            ].map((t, i) => (
              <ScrollReveal key={i} delay={i * 0.12}>
                <div className="bg-[#F5F0E8] p-7 sm:p-8 h-full flex flex-col border border-black/10">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} size={14} fill="#B80000" stroke="#B80000" />
                    ))}
                  </div>
                  <p className="font-serif text-xl leading-[1.4] text-black mb-6 flex-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="pt-4 border-t border-black/10">
                    <p className="text-sm font-semibold text-black">{t.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/40 mt-1">{t.location}</p>
                    <p className="text-[10px] text-black/35 mt-1.5 italic">{t.product}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Recently Viewed ═══ */}
      {recentlyViewed.length > 0 && (
        <section data-testid="recently-viewed-section" className="py-16 sm:py-24 border-t border-black/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal>
              <div className="mb-12">
                <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-black/25 mb-3">Continuez votre découverte</p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Vu récemment
                </h2>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
              {recentlyViewed.slice(0, 4).map((p, i) => (
                <ScrollReveal key={p.product_id} delay={i * 0.08}>
                  <ProductCard product={p} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ CTA ═══ */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.4em] font-bold text-black/25 mb-6">Commencez Maintenant</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Votre peau m&eacute;rite<br/>le <span className="italic" style={{ color: '#B80000' }}>meilleur</span>
              </h2>
              <div className="mt-10">
                <Link to="/shop" className="btn-primary inline-block">
                  Explorer la Boutique
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
